/*
  © 2024 KondaSoft
  https://www.kondasoft.com
*/

let collectionAbortController

const collectionRefinementPayload = (section, filters, requestUrl, action) => {
  const url = new URL(requestUrl, window.location.origin)
  const activeFilters = {}
  filters.querySelectorAll('[name^="filter."]').forEach(control => {
    if (control.disabled || ((control.type === 'checkbox' || control.type === 'radio') && !control.checked)) return
    const key = control.name
    if (!key.startsWith('filter.')) return
    const values = (control.selectedOptions ? [...control.selectedOptions].filter(option => option.selected).map(option => option.value) : [control.value])
      .map(value => String(value).trim().slice(0, 80)).filter(Boolean)
    if (!values.length) return
    const boundedKey = key.slice(0, 80)
    if (Object.keys(activeFilters).length >= 6 && !activeFilters[boundedKey]) return
    if (!activeFilters[boundedKey]) activeFilters[boundedKey] = []
    values.forEach(value => {
      if (activeFilters[boundedKey].length < 3 && !activeFilters[boundedKey].includes(value)) activeFilters[boundedKey].push(value)
    })
  })
  const sortBy = filters.querySelector('[name="sort_by"][type="hidden"]')?.value
  const page = parseInt(url.searchParams.get('page'), 10)
  const allowedSorts = ['manual', 'best-selling', 'title-ascending', 'title-descending', 'price-ascending', 'price-descending', 'created-ascending', 'created-descending', 'rating']
  return {
    collection_handle: (section.getAttribute('data-collection-handle') || '').slice(0, 100),
    action: ['filter', 'sort', 'page', 'clear'].includes(action) ? action : 'filter',
    active_filters: activeFilters,
    sort_by: allowedSorts.includes(sortBy) ? sortBy : null,
    page: Number.isSafeInteger(page) && page > 0 && page <= 999 ? page : 1,
    result_count: Number.isSafeInteger(parseInt(section.getAttribute('data-result-count'), 10)) ? parseInt(section.getAttribute('data-result-count'), 10) : null
  }
}

const loadCollection = async (action = 'filter') => {
  const collectionProducts = document.querySelector('.collection-products')
  const productList = collectionProducts.querySelector('.product-list')
  const filters = document.querySelector('.collection-filters')
  const controller = new AbortController()
  const requestUrl = window.location.href

  collectionAbortController?.abort()
  collectionAbortController = controller

  if (productList) {
    productList.style.opacity = '.2'
  }

  try {
    const response = await fetch(requestUrl, { signal: controller.signal })
    if (!response.ok) throw new Error('Failed to load collection')

    const data = await response.text()
    const parser = new DOMParser()
    const newDocument = parser.parseFromString(data, 'text/html')
    const newCollectionProducts = newDocument.querySelector('.collection-products')
    const newFilters = newDocument.querySelector('.collection-filters')

    if (!newCollectionProducts || !newFilters) throw new Error('Invalid collection response')
    if (collectionAbortController !== controller) return

    collectionProducts.replaceWith(newCollectionProducts)
    filters.replaceWith(newFilters)

    await refineBudgetCollection()
    if (collectionAbortController !== controller) return

    window.dispatchEvent(new CustomEvent('kt.collection.loaded'))
    window.dispatchEvent(new CustomEvent('ws:track', {
      detail: {
        type: 'collection_refinement_applied',
        payload: collectionRefinementPayload(newCollectionProducts, newFilters, requestUrl, action)
      }
    }))

    const navbarHeight = document.querySelector('[id*="__navbar"].sticky-top')?.clientHeight || 0
    newCollectionProducts.style.scrollMarginTop = `${navbarHeight}px`
    newCollectionProducts.scrollIntoView()
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error('Failed to load collection:', error)
      if (collectionAbortController === controller && productList) productList.style.opacity = ''
    }
  } finally {
    if (collectionAbortController === controller) collectionAbortController = null
  }
}

// Point the URL at a new query string and re-render the collection via AJAX.
const reloadCollection = (params, action = 'filter') => {
  const search = params.toString()
  window.history.replaceState({}, '', `${window.location.pathname}${search ? '?' + search : ''}`)
  loadCollection(action)
}

// Build query params without blank form controls. In particular, untouched
// price boundaries must not become an active $0-to-maximum price filter.
const paramsFromForm = (form) => {
  const params = new URLSearchParams()
  for (const [key, value] of new FormData(form).entries()) {
    if (String(value).trim() !== '') params.append(key, value)
  }
  return params
}

// Drop every active facet ("filter.*") and the page cursor from the params.
const clearFilterParams = (params) => {
  for (const key of [...params.keys()]) {
    if (key.includes('filter.')) {
      params.delete(key)
    }
  }
  params.delete('page')
  return params
}

// "Top rated" is a client-side sort: Shopify can't order a collection by a
// review metafield, so we reorder the loaded cards by their data-rating (from
// the Shopify standard `reviews.rating` metafield — Judge.me must sync into it,
// see docs/search-discovery-filters.md). It applies to the products currently
// loaded and re-runs after Load more, so the order extends as more load.
// Products with no reviews sort last.
const applyRatingSort = () => {
  if (new URLSearchParams(window.location.search).get('sort_by') !== 'rating') return
  const grid = document.querySelector('.product-list')
  if (!grid) return

  const score = (item) => {
    const card = item.querySelector('[data-product-card]') || item
    const count = parseInt(card.getAttribute('data-rating-count'), 10) || 0
    const rating = parseFloat(card.getAttribute('data-rating')) || 0
    return count > 0 ? rating + count / 1e6 : -1
  }

  const items = [...grid.querySelectorAll(':scope > [role="list-item"]')]
  items.sort((a, b) => score(b) - score(a))
  items.forEach(item => grid.appendChild(item))
}

window.addEventListener('kt.collection.loaded', applyRatingSort)
document.addEventListener('DOMContentLoaded', applyRatingSort)

// Under $10 needs an available option within the AUD budget, across every page.
const budgetPriceLimit = (currency, rate, activeCurrency) => {
  if (currency === 'AUD') return 1000
  return activeCurrency === currency && Number.isFinite(rate) && rate > 0 ? Math.round(1000 * rate) : null
}

const refineBudgetCollection = async () => {
  const section = document.querySelector('.collection-products[data-collection-handle="under-10"]')
  if (!section || section.dataset.budgetRefined) return
  section.dataset.budgetRefined = 'loading'
  const location = window.location.href.split('#')[0]
  const current = () => section.isConnected && window.location.href.split('#')[0] === location
  const limit = budgetPriceLimit(section.dataset.currency, Number(window.Shopify?.currency?.rate), window.Shopify?.currency?.active)
  const grid = section.querySelector('.product-list')
  const loader = section.querySelector('collection-load-more')
  const count = section.querySelector('.collection-product-count')
  const filterButton = document.querySelector('.btn-filters-view-results')
  const filterItems = () => {
    grid?.querySelectorAll(':scope > [role="list-item"]').forEach(item => {
      const price = Number(item.dataset.budgetPrice)
      if (limit === null || !Number.isFinite(price) || price >= limit) item.remove()
      else item.hidden = false
    })
  }
  try {
    if (!grid || limit === null) throw new Error('Budget currency rate unavailable')
    filterItems()
    // A bookmarked later page must include earlier eligible products in its total.
    if (new URL(window.location.href).searchParams.has('page')) {
      const url = new URL(window.location.href)
      url.searchParams.delete('page')
      window.location.replace(url.href)
      return
    }
    while (loader?.button?.getAttribute('href')) {
      if (!await loader.loadNext(true) || !current()) return
      filterItems()
    }
    if (!current()) return
    const total = grid.querySelectorAll(':scope > [role="list-item"]').length
    section.dataset.resultCount = String(total)
    section.dataset.budgetRefined = 'complete'
    if (count) count.textContent = `${total} available budget picks`
    if (filterButton) filterButton.textContent = `View ${total} results`
    if (loader?.statusEl) loader.statusEl.textContent = `Showing ${total} of ${total}`
    if (!total) {
      const empty = document.createElement('p')
      empty.className = 'alert alert-primary'
      empty.textContent = 'No available options under $10 AUD match these filters.'
      grid.appendChild(empty)
    }
  } catch (error) {
    if (!current()) return
    filterItems()
    section.dataset.budgetRefined = 'failed'
    if (count) count.textContent = 'Budget picks could not be fully loaded. Refresh to try again.'
    if (filterButton) filterButton.textContent = 'View loaded budget picks'
  }
}
document.addEventListener('DOMContentLoaded', refineBudgetCollection)

class CollectionFilters extends HTMLElement {
  constructor () {
    super()

    this.form = this.querySelector('form')
    if (!this.form) return

    this.initWiggleIntensityFilters()
    this.initPriceFilters()

    this.form.querySelectorAll('input').forEach(input => {
      input.addEventListener('change', () => {
        reloadCollection(paramsFromForm(this.form))
      })
    })

    this.querySelectorAll('.btn-filters-clear-all').forEach(btn => {
      for (const key of new URLSearchParams(window.location.search).keys()) {
        if (key.includes('filter.')) {
          btn.removeAttribute('hidden')
        }
      }

      btn.addEventListener('click', () => {
        reloadCollection(clearFilterParams(new URLSearchParams(window.location.search)), 'clear')
      })
    })
  }

  initWiggleIntensityFilters () {
    this.form.querySelectorAll('[data-wiggle-filter]').forEach(filter => {
      const slider = filter.querySelector('[data-wiggle-slider]')
      const values = filter.querySelector('[data-wiggle-values]')
      const minInput = filter.querySelector('[data-wiggle-min]')
      const maxInput = filter.querySelector('[data-wiggle-max]')
      const output = filter.querySelector('[data-wiggle-output]')
      const fill = filter.querySelector('[data-wiggle-fill]')
      const valueInputs = [...filter.querySelectorAll('[data-wiggle-level]')]

      if (!slider || !values || !minInput || !maxInput || !output || !fill || !valueInputs.length) return

      const syncRange = (changedInput, applyValues = true) => {
        let minimum = parseInt(minInput.value, 10)
        let maximum = parseInt(maxInput.value, 10)

        if (minimum > maximum) {
          if (changedInput === minInput) {
            maximum = minimum
            maxInput.value = String(maximum)
          } else {
            minimum = maximum
            minInput.value = String(minimum)
          }
        }

        if (applyValues) {
          const isFullRange = minimum === 1 && maximum === 10
          valueInputs.forEach(input => {
            const level = parseInt(input.getAttribute('data-wiggle-level'), 10)
            input.checked = !isFullRange && !input.disabled && level >= minimum && level <= maximum
          })
        }

        const minimumPercent = ((minimum - 1) / 9) * 100
        const maximumPercent = ((maximum - 1) / 9) * 100
        fill.style.left = `${minimumPercent}%`
        fill.style.width = `${maximumPercent - minimumPercent}%`
        output.textContent = minimum === maximum
          ? `${minimum} / 10`
          : `${minimum}-${maximum}`
      }

      minInput.addEventListener('input', () => syncRange(minInput))
      maxInput.addEventListener('input', () => syncRange(maxInput))
      minInput.addEventListener('change', () => syncRange(minInput))
      maxInput.addEventListener('change', () => syncRange(maxInput))

      values.hidden = true
      slider.hidden = false
      syncRange(null, false)
    })
  }

  initPriceFilters () {
    this.form.querySelectorAll('[data-price-filter]').forEach(filter => {
      const slider = filter.querySelector('[data-price-slider]')
      const minInput = filter.querySelector('[data-price-min]')
      const maxInput = filter.querySelector('[data-price-max]')
      const minField = filter.querySelector('[data-price-min-field]')
      const maxField = filter.querySelector('[data-price-max-field]')
      const output = filter.querySelector('[data-price-output]')
      const fill = filter.querySelector('[data-price-fill]')
      const rangeMaximum = parseFloat(filter.getAttribute('data-range-max'))

      if (!slider || !minInput || !maxInput || !minField || !maxField || !output || !fill || !rangeMaximum) return

      const currency = filter.getAttribute('data-currency') || 'AUD'
      const locale = filter.getAttribute('data-locale') || 'en-AU'
      const moneyFormatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 2
      })
      const clamp = (value) => Math.min(rangeMaximum, Math.max(0, value))
      const numberOr = (value, fallback) => {
        const parsed = parseFloat(value)
        return Number.isFinite(parsed) ? parsed : fallback
      }
      const inputValue = (value) => {
        const rounded = Math.round(value * 100) / 100
        return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2)
      }

      const syncRange = (changedInput, applyFields = true) => {
        let minimum = clamp(numberOr(minInput.value, 0))
        let maximum = clamp(numberOr(maxInput.value, rangeMaximum))

        if (minimum > maximum) {
          if (changedInput === minInput) {
            maximum = minimum
            maxInput.value = inputValue(maximum)
          } else {
            minimum = maximum
            minInput.value = inputValue(minimum)
          }
        }

        if (applyFields) {
          minField.value = minimum <= 0 ? '' : inputValue(minimum)
          maxField.value = maximum >= rangeMaximum ? '' : inputValue(maximum)
        }

        const minimumPercent = (minimum / rangeMaximum) * 100
        const maximumPercent = (maximum / rangeMaximum) * 100
        const minimumText = moneyFormatter.format(minimum)
        const maximumText = moneyFormatter.format(maximum)

        fill.style.left = `${minimumPercent}%`
        fill.style.width = `${maximumPercent - minimumPercent}%`
        output.textContent = `${minimumText}-${maximumText}`
        minInput.setAttribute('aria-valuetext', minimumText)
        maxInput.setAttribute('aria-valuetext', maximumText)
      }

      const syncFields = (changedField) => {
        const minimum = minField.value === '' ? 0 : clamp(numberOr(minField.value, 0))
        const maximum = maxField.value === '' ? rangeMaximum : clamp(numberOr(maxField.value, rangeMaximum))
        minInput.value = inputValue(minimum)
        maxInput.value = inputValue(maximum)
        syncRange(changedField === minField ? minInput : maxInput)
      }

      minInput.addEventListener('input', () => syncRange(minInput))
      maxInput.addEventListener('input', () => syncRange(maxInput))
      minInput.addEventListener('change', () => syncRange(minInput))
      maxInput.addEventListener('change', () => syncRange(maxInput))
      minField.addEventListener('input', () => syncFields(minField))
      maxField.addEventListener('input', () => syncFields(maxField))
      minField.addEventListener('change', () => syncFields(minField))
      maxField.addEventListener('change', () => syncFields(maxField))

      slider.hidden = false
      syncRange(null, false)
    })
  }
}
customElements.define('collection-filters', CollectionFilters)

// Removable "applied filters" chips shown above the product grid. Each chip is
// a real link (works without JS); here we intercept it into the same AJAX
// refresh used by the filter drawer. The element re-upgrades whenever
// loadCollection() replaces .collection-products, so handlers rebind for free.
class AppliedFilters extends HTMLElement {
  constructor () {
    super()

    this.querySelectorAll('[data-applied-filter]').forEach(link => {
      link.addEventListener('click', (event) => {
        event.preventDefault()
        const href = link.getAttribute('href')
        if (!href) return
        reloadCollection(new URLSearchParams(new URL(href, window.location.origin).search))
      })
    })

    this.querySelectorAll('[data-applied-clear]').forEach(btn => {
      btn.addEventListener('click', (event) => {
        event.preventDefault()
        reloadCollection(clearFilterParams(new URLSearchParams(window.location.search)), 'clear')
      })
    })
  }
}
customElements.define('applied-filters', AppliedFilters)

// Progressive-enhancement "Load more" for the product grid. The button is a
// real rel="next" link (works without JS); here we fetch the next page and
// append its cards. As a custom element it re-upgrades whenever
// loadCollection() swaps the section, so it keeps working after filtering.
class CollectionLoadMore extends HTMLElement {
  constructor () {
    super()

    this.button = this.querySelector('[data-load-more]')
    this.statusEl = this.querySelector('[data-load-more-status]')
    this.total = parseInt(this.getAttribute('data-total'), 10) || 0
    this.updateStatus()

    this.button?.addEventListener('click', (event) => {
      event.preventDefault()
      this.loadNext()
    })
  }

  get grid () {
    return document.querySelector('.product-list')
  }

  updateStatus () {
    if (!this.statusEl || !this.total || !this.grid) return
    const shown = this.grid.querySelectorAll(':scope > [role="list-item"]').length
    this.statusEl.textContent = `Showing ${shown} of ${this.total}`
  }

  disconnectedCallback () {
    this.requestVersion = (this.requestVersion || 0) + 1
    if (this.button?.getAttribute('aria-busy') !== 'true') return
    this.button.textContent = this.loadingLabel
    this.button.classList.remove('disabled')
    this.button.removeAttribute('aria-busy')
  }

  async loadNext (automatic = false) {
    const url = this.button?.getAttribute('href')
    if (!url || this.button.getAttribute('aria-busy') === 'true' || !this.grid) return

    const version = this.requestVersion = (this.requestVersion || 0) + 1
    const grid = this.grid
    const location = window.location.href.split('#')[0]
    const isCurrent = () => version === this.requestVersion && this.isConnected && grid.isConnected && this.grid === grid && window.location.href.split('#')[0] === location
    const shownBefore = grid.querySelectorAll(':scope > [role="list-item"]').length
    const requestedPage = parseInt(new URL(url, window.location.origin).searchParams.get('page'), 10) || null
    if (!automatic) {
      window.dispatchEvent(new CustomEvent('ws:track', {
        detail: {
          type: 'collection_load_more_clicked',
          payload: {
            collection_handle: this.getAttribute('data-collection-handle') || '',
            shown_before: shownBefore,
            total_count: this.total,
            requested_page: requestedPage
          }
        }
      }))
    }

    const label = this.loadingLabel = this.button.textContent
    this.button.setAttribute('aria-busy', 'true')
    this.button.classList.add('disabled')
    this.button.textContent = 'Loading…'

    try {
      const response = await fetch(url, { headers: { 'X-Requested-With': 'fetch' } })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html')

      if (!isCurrent()) return

      const nextGrid = doc.querySelector('.product-list')
      if (nextGrid) {
        const fragment = document.createDocumentFragment()
        nextGrid.querySelectorAll(':scope > [role="list-item"]').forEach(node => {
          fragment.appendChild(document.importNode(node, true))
        })
        grid.appendChild(fragment)
      }

      const nextButton = doc.querySelector('[data-load-more]')
      if (nextButton) {
        this.button.setAttribute('href', nextButton.getAttribute('href'))
        this.button.textContent = label
        this.button.classList.remove('disabled')
        this.button.removeAttribute('aria-busy')
      } else {
        this.button.removeAttribute('href')
        this.button.remove()
      }
      this.updateStatus()
      applyRatingSort()
      return true
    } catch (error) {
      if (automatic) {
        if (isCurrent()) {
          this.button.textContent = label
          this.button.classList.remove('disabled')
          this.button.removeAttribute('aria-busy')
        }
        throw error
      }
      if (isCurrent()) window.location.href = url
    }
  }
}
customElements.define('collection-load-more', CollectionLoadMore)

class SortBy extends HTMLElement {
  constructor () {
    super()

    // Reflect the active sort in the menu. Native sorts are marked server-side,
    // but the custom "rating" value isn't, so sync it from the URL here.
    const current = new URLSearchParams(window.location.search).get('sort_by')
    if (current) {
      const active = this.querySelector(`input[value="${current}"]`)
      if (active) active.checked = true
    }

    this.querySelectorAll('input').forEach(input => {
      input.addEventListener('change', () => {
        this.setUrl(input.value)
        loadCollection('sort')
      })
    })
  }

  setUrl (value) {
    const params = new URLSearchParams(window.location.search)
    params.set('sort_by', value)
    params.delete('page')
    const url = `${window.location.pathname}?${params.toString()}`
    window.history.replaceState({}, '', url)
  }
}
customElements.define('sort-by', SortBy)

class Pagination extends HTMLElement {
  constructor () {
    super()

    this.querySelectorAll('a').forEach(elem => {
      elem.addEventListener('click', (event) => {
        event.preventDefault()

        this.setUrl(elem.dataset.newPage)
        loadCollection('page')
      })
    })
  }

  setUrl (value) {
    const params = new URLSearchParams(window.location.search)
    params.set('page', value)
    const url = `${window.location.pathname}?${params.toString()}`
    window.history.replaceState({}, '', url)
  }
}
customElements.define('collection-pagination', Pagination)

if (typeof module !== 'undefined') module.exports = { collectionRefinementPayload, loadCollection }
